import React, { useContext, useEffect, useState } from "react";
import "./CallToBook.css";
import { Link } from "react-router-dom";
import {
  dropShadowLowStyle,
  spacingFluidLarge,
  spacingFluidSmall,
  spacingFluidXSmall,
} from "@porsche-design-system/components-js/styles";
import { BorderBeam } from "@/components/magicui/border-beam";
import { useGetTileBaseStyleDrop } from "MainApp/assets/visual/styles/globalStyles/globalJsxStyles";
import VitalInfoComponent from "Components/VitalInfoComponent/VitalInfoComponent";
import styled from "styled-components";
import {
  getSkeletonStyle,
  fontLineHeight,
  fontSizeTextMedium,
  spacingFluidMedium,
} from "@porsche-design-system/components-react/styles";
import { IsRotatedContext } from "MainApp/assets/utils/context/isRotatedContext";
import { useTranslation } from "react-i18next";
import { AuthContext } from "MainApp/assets/utils/context/AuthContext";
import { getIntlLocale } from "MainApp/assets/utils/js/localeUtils";
import CButton from "Components/CComponents/CButton/CButton";
import CText from "Components/CComponents/CText/CText";

// Definicion de Skeletons
const SkeletonLight = styled.div(getSkeletonStyle({ theme: "light" }));
const SkeletonTextLight = styled(SkeletonLight)({
  height: fontLineHeight,
  width: "60%",
  fontSize: fontSizeTextMedium,
});

const CallToBook = ({ tour, isSimple, isInfo, isPhone }) => {
  const { isRotated } = useContext(IsRotatedContext);
  const { axiosInstance, isAuthReady, token, optionalFields, isTuk } =
    useContext(AuthContext);

  const [nextTour, setNextTour] = useState(0);
  const [isFirst, setIsFirst] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { t, i18n } = useTranslation("callToBook");

  // Si tour.id es undefined, cacheKey sera "nextTour_undefined"
  const cacheKey = `nextTour_${tour?.id}`;

  const calculateTimeRemaining = (tourDateEpoch) => {
    const now = Date.now();
    const timeDifference = tourDateEpoch - now;

    if (timeDifference > 0) {
      const total = timeDifference;
      const days = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (timeDifference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const minutes = Math.floor(
        (timeDifference % (1000 * 60 * 60)) / (1000 * 60)
      );
      const seconds = Math.floor((timeDifference % (1000 * 60)) / 1000);

      return { total, days, hours, minutes, seconds };
    } else {
      return { total: 0, days: 0, hours: 0, minutes: 0, seconds: 0 };
    }
  };

  const fetchNextTour = async () => {
    try {
      if (!tour || !tour.id) {
        throw new Error("No tour data available");
      }

      const now = Date.now();
      const cachedNextTour = localStorage.getItem(cacheKey);

      if (cachedNextTour) {
        const cachedData = JSON.parse(cachedNextTour);
        const { nextTourEpoch } = cachedData;

        if (nextTourEpoch > now) {
          setNextTour(nextTourEpoch);
          setTimeRemaining(calculateTimeRemaining(nextTourEpoch));
          setLoading(false);
          return;
        }
      }

      const tourUUID = tour.id;
      const response = await axiosInstance.get(
        `/toursAPI/${tourUUID}/next-available-slot`
      );

      if (!response.data || !response.data.next_available_slot) {
        throw new Error("No next available slot found");
      }

      const newNextTour = response.data.next_available_slot * 1000;
      setNextTour(newNextTour);

      // Cacheamos el resultado
      localStorage.setItem(
        cacheKey,
        JSON.stringify({ nextTourEpoch: newNextTour })
      );

      setTimeRemaining(calculateTimeRemaining(newNextTour));
      setLoading(false);
    } catch (err) {
      console.error("Error fetching next tour:", err);
      setError(err.message);
      setLoading(false);
    }
  };

  const handleCountdownEnd = () => {
    setLoading(true);
    setTimeRemaining(null);

    if (tour && isAuthReady && token && axiosInstance) {
      fetchNextTour();
    }
  };

  useEffect(() => {
    // Solo en la primera carga, si tenemos todo listo, tiramos la peticion
    if (isFirst) {
      if (tour && isAuthReady && token && axiosInstance) {
        fetchNextTour();
        setIsFirst(false);
      }
    }

    // Creamos el intervalo para actualizar la cuenta atras cada segundo
    const interval = setInterval(() => {
      if (nextTour) {
        const newTimeRemaining = calculateTimeRemaining(nextTour);
        setTimeRemaining(newTimeRemaining);

        if (newTimeRemaining.total <= 0) {
          clearInterval(interval);
          handleCountdownEnd();
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [tour, isAuthReady, token, axiosInstance, nextTour]);

  console.log("tour", tour);

  const getTitleString = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value.trim();
    if (typeof value === "object") {
      const first = Object.values(value).find(
        (v) => typeof v === "string" && v.trim()
      );
      return first ? first.trim() : "";
    }
    return "";
  };

  const tourTitle = getTitleString(tour?.title || tour?.name);
  const normalizePeoplePrices = (ranges) =>
    (Array.isArray(ranges) ? ranges : []).map((range) => ({
      min_people: Number(range?.min_people ?? range?.minPeople ?? 1) || 1,
      max_people: (() => {
        const rawMax = range?.max_people ?? range?.maxPeople;
        if (rawMax === "" || rawMax == null) return null;
        const parsed = Number(rawMax);
        return Number.isFinite(parsed) ? parsed : null;
      })(),
      price: Number(range?.price ?? 0) || 0,
    }));

  const normalizeDurations = (durations) =>
    (Array.isArray(durations) ? durations : []).map((d) => ({
      ...d,
      duration: Number(d?.duration ?? 0) || 0,
      price: Number(d?.price ?? 0) || 0,
      stripe_price_id: d?.stripe_price_id ?? d?.stripePriceId ?? "",
      people_prices: normalizePeoplePrices(d?.people_prices ?? d?.peoplePrices),
    }));

  // Flag interna: si se pone en false, deja de ordenar por minutos.
  const SORT_DURATIONS_BY_MINUTES = true;
  const sortDurationsByMinutes = (durations, enabled = true) => {
    if (!enabled) return durations;
    return [...durations].sort((a, b) => {
      const aMinutes = Number(a?.duration ?? 0) || 0;
      const bMinutes = Number(b?.duration ?? 0) || 0;
      if (aMinutes !== bMinutes) return aMinutes - bMinutes;

      const aPosition = Number(a?.position ?? Number.MAX_SAFE_INTEGER);
      const bPosition = Number(b?.position ?? Number.MAX_SAFE_INTEGER);
      if (aPosition !== bPosition) return aPosition - bPosition;

      return Number(a?.id ?? 0) - Number(b?.id ?? 0);
    });
  };

  const formatPrice = (value) => {
    const amount = Number(value ?? 0);
    if (!Number.isFinite(amount)) return "0";
    return new Intl.NumberFormat(getIntlLocale(i18n.language), {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const getDurationPriceLabel = (durationData) => {
    const rangePrices = (Array.isArray(durationData?.people_prices)
      ? durationData.people_prices
      : []
    )
      .map((range) => Number(range?.price ?? 0))
      .filter((price) => Number.isFinite(price))
      .sort((a, b) => a - b);

    if (rangePrices.length) {
      const minPrice = rangePrices[0];
      const maxPrice = rangePrices[rangePrices.length - 1];
      if (minPrice === maxPrice) return `(${formatPrice(minPrice)} \u20AC)`;
      return `(${formatPrice(minPrice)} - ${formatPrice(maxPrice)} \u20AC)`;
    }

    return `(${formatPrice(durationData?.price)} \u20AC)`;
  };

  const sortPeopleRanges = (ranges) =>
    [...(Array.isArray(ranges) ? ranges : [])]
      .map((range) => ({
        min_people: Number(range?.min_people ?? range?.minPeople ?? 1) || 1,
        max_people: (() => {
          const rawMax = range?.max_people ?? range?.maxPeople;
          if (rawMax === "" || rawMax == null) return null;
          const parsed = Number(rawMax);
          return Number.isFinite(parsed) ? parsed : null;
        })(),
        price: Number(range?.price ?? 0) || 0,
      }))
      .sort((a, b) => {
        if (a.min_people !== b.min_people) return a.min_people - b.min_people;

        const aHasMax = Number.isFinite(a.max_people);
        const bHasMax = Number.isFinite(b.max_people);
        if (aHasMax && bHasMax && a.max_people !== b.max_people) {
          return a.max_people - b.max_people;
        }
        if (aHasMax !== bHasMax) return aHasMax ? -1 : 1;

        return a.price - b.price;
      });

  const formatPeopleRangeLabel = (minPeopleRaw, maxPeopleRaw) => {
    const minPeople = Math.max(1, Number(minPeopleRaw ?? 1) || 1);
    const parsedMax = Number(maxPeopleRaw);
    const hasMax = Number.isFinite(parsedMax) && parsedMax >= minPeople;
    const peopleText = t("people") || "personas";

    if (!hasMax) return `${minPeople}+ ${peopleText}`;
    if (parsedMax === minPeople) return `${minPeople} ${peopleText}`;
    return `${minPeople}-${parsedMax} ${peopleText}`;
  };

  const getPeoplePricingRows = (durations, idx) => {
    const list = Array.isArray(durations) ? durations : [];
    const current = list[idx];
    if (!current) return [];

    const explicitRanges = sortPeopleRanges(current?.people_prices);
    if (explicitRanges.length) {
      return explicitRanges.map((range) => ({
        label: formatPeopleRangeLabel(range.min_people, range.max_people),
        min_people: range.min_people,
        max_people: range.max_people,
        price: range.price,
      }));
    }

    const minPeople = Math.max(1, Number(current?.duration ?? 1) || 1);
    const nextMinRaw = Number(list[idx + 1]?.duration ?? 0);
    const inferredMax =
      Number.isFinite(nextMinRaw) && nextMinRaw > minPeople
        ? nextMinRaw - 1
        : null;

    return [
      {
        label: formatPeopleRangeLabel(minPeople, inferredMax),
        min_people: minPeople,
        max_people: inferredMax,
        price: Number(current?.price ?? 0) || 0,
      },
    ];
  };

  const isPeopleMode = Boolean(
    tour?.personsInsteadTime ?? tour?.uses_people_instead_of_times
  );

  const normalizedDurations = sortDurationsByMinutes(
    normalizeDurations(tour?.durations),
    SORT_DURATIONS_BY_MINUTES
  );
  const usesPeopleAndTimesPricing = Boolean(
    tour?.usesPeopleAndTimesPricing ?? tour?.uses_people_and_times_pricing
  );
  const frontendUsesPeoplePriceTiers = Boolean(
    tour?.frontendUsesPeoplePriceTiers ??
      tour?.frontend_uses_people_price_tiers
  );
  const usePeopleTierButtonsInFrontend = Boolean(
    isPeopleMode &&
      usesPeopleAndTimesPricing &&
      frontendUsesPeoplePriceTiers
  );

  const peopleModeButtonRows = usePeopleTierButtonsInFrontend
    ? normalizedDurations.flatMap((d, idx) => {
        const peoplePricingRows = getPeoplePricingRows(normalizedDurations, idx);
        const durationTourBase = {
          ...tour,
          sprice: d.stripe_price_id,
          duration: d.duration,
          selectedDuration: d.duration,
          durations: normalizedDurations,
          vehiclesSmall: tour.vehiclesSmall || false,
          personsInsteadTime: isPeopleMode,
          usesPeopleAndTimesPricing,
          uses_people_and_times_pricing: usesPeopleAndTimesPricing,
          preferLowerPeoplePriceTier: Boolean(
            tour?.preferLowerPeoplePriceTier ??
              tour?.prefer_lower_people_price_tier
          ),
          prefer_lower_people_price_tier: Boolean(
            tour?.preferLowerPeoplePriceTier ??
              tour?.prefer_lower_people_price_tier
          ),
          frontendUsesPeoplePriceTiers,
          frontend_uses_people_price_tiers: frontendUsesPeoplePriceTiers,
        };

        return peoplePricingRows.map((row, rowIdx) => {
          const parsedMax = Number(row.max_people);
          const hasMax = Number.isFinite(parsedMax) && parsedMax > 0;
          const selectedPeopleCount = hasMax
            ? parsedMax
            : Math.max(1, Number(row.min_people ?? 1) || 1);

          return {
            key: `${d.id ?? d.duration}-tier-${rowIdx}-${row.label}`,
            duration: d.duration,
            label: row.label,
            price: row.price,
            durationTour: {
              ...durationTourBase,
              price: row.price,
              selectedPeopleCount,
              persons: selectedPeopleCount,
              selectedPeopleRange: {
                min_people: row.min_people,
                max_people: selectedPeopleCount,
                price: row.price,
              },
            },
          };
        });
      })
    : [];

  // Botones de duracion si hay mas de una opcion
  const hasMultipleDurations = usePeopleTierButtonsInFrontend
    ? peopleModeButtonRows.length > 1
    : normalizedDurations.length > 1;
  const renderDurationButtons = () => {
    return (
      <>
        <CText size="large" align="center">
          {t("headDurations") || "Selecciona duracion y precio"}
        </CText>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            gap: spacingFluidSmall,
            width: "100%",
          }}
        >
          {usePeopleTierButtonsInFrontend
            ? peopleModeButtonRows.map((row) => {
                const query = new URLSearchParams({
                  duration: String(row.duration),
                });
                const selectedPeopleCount = Number(
                  row.durationTour?.selectedPeopleCount ??
                    row.durationTour?.selected_people_count ??
                    0
                );
                const selectedMinPeople = Number(
                  row.durationTour?.selectedPeopleRange?.min_people ??
                    row.durationTour?.selected_people_range?.min_people ??
                    0
                );
                const selectedMaxPeople = Number(
                  row.durationTour?.selectedPeopleRange?.max_people ??
                    row.durationTour?.selected_people_range?.max_people ??
                    0
                );

                if (
                  Number.isFinite(selectedPeopleCount) &&
                  selectedPeopleCount > 0
                ) {
                  query.set("people", String(selectedPeopleCount));
                }
                if (Number.isFinite(selectedMinPeople) && selectedMinPeople > 0) {
                  query.set("peopleMin", String(selectedMinPeople));
                }
                if (Number.isFinite(selectedMaxPeople) && selectedMaxPeople > 0) {
                  query.set("peopleMax", String(selectedMaxPeople));
                }

                return (
                  <Link
                    key={row.key}
                    to={`/reservation/${tour.slug}?${query.toString()}`}
                    state={{
                      title: getTitleString(
                        row.durationTour?.title || tourTitle
                      ),
                      tour: row.durationTour,
                    }}
                    className="call-to-book-duration-link"
                    style={{ textDecoration: "none" }}
                  >
                    <CButton
                      className="call-to-book-duration-button"
                      theme="dark"
                      stretch={true}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <CText>{row.duration} min.</CText>
                        <CText>
                          {row.label} ({formatPrice(row.price)} {"\u20AC"})
                        </CText>
                      </div>
                    </CButton>
                  </Link>
                );
              })
            : normalizedDurations.map((d) => {
                const durationPriceLabel = getDurationPriceLabel(d);
                const durationTour = {
                  ...tour,
                  sprice: d.stripe_price_id,
                  duration: d.duration,
                  price: d.price,
                  selectedDuration: d.duration,
                  durations: normalizedDurations,
                  vehiclesSmall: tour.vehiclesSmall || false,
                  personsInsteadTime: isPeopleMode,
                  usesPeopleAndTimesPricing,
                  uses_people_and_times_pricing: usesPeopleAndTimesPricing,
                  preferLowerPeoplePriceTier: Boolean(
                    tour?.preferLowerPeoplePriceTier ??
                      tour?.prefer_lower_people_price_tier
                  ),
                  prefer_lower_people_price_tier: Boolean(
                    tour?.preferLowerPeoplePriceTier ??
                      tour?.prefer_lower_people_price_tier
                  ),
                  frontendUsesPeoplePriceTiers,
                  frontend_uses_people_price_tiers:
                    frontendUsesPeoplePriceTiers,
                };

                return (
                  <Link
                    key={d.id ?? `duration-${d.duration}`}
                    to={`/reservation/${tour.slug}?duration=${d.duration}`}
                    state={{
                      title: getTitleString(durationTour?.title || tourTitle),
                      tour: durationTour,
                    }}
                    className="call-to-book-duration-link"
                    style={{ textDecoration: "none" }}
                  >
                    <CButton
                      className="call-to-book-duration-button"
                      theme="dark"
                      stretch={true}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <CText>{d.duration} min.</CText>
                        <CText>{durationPriceLabel}</CText>
                      </div>
                    </CButton>
                  </Link>
                );
              })}
        </div>
      </>
    );
  };
  const countdownStyle = {
    marginTop: isSimple ? spacingFluidXSmall : "0px",
  };
  const internalCountdownStyle = {
    size: "x-large",
    align: "center",
  };
  const tourReservationStyling = {
    position: isPhone ? "relative" : "sticky",
    top: isRotated ? "2.5svw" : "2.5svh",
    padding: spacingFluidMedium,
    margin: isPhone
      ? `0 ${spacingFluidLarge} ${spacingFluidMedium} ${spacingFluidLarge}`
      : `${spacingFluidSmall} ${spacingFluidMedium} 0 ${spacingFluidMedium}`,
    gap: hasMultipleDurations ? spacingFluidSmall : 0,
  };
  const normalizeTour = (raw) => {
    if (!raw) return null;

    const durations = sortDurationsByMinutes(
      normalizeDurations(raw.durations),
      SORT_DURATIONS_BY_MINUTES
    );
    const hasDurations = durations.length;
    console.log("Duraciones disponibles:", hasDurations);

    const sprice =
      hasDurations === 1
        ? durations[0].stripe_price_id // unica duracion
        : raw.stripe_price_id || ""; // fallback (o "")
    console.log("raw calculated:", raw);
    const usesPeopleAndTimesPricingRaw = Boolean(
      raw.usesPeopleAndTimesPricing ?? raw.uses_people_and_times_pricing
    );
    const frontendUsesPeoplePriceTiersRaw = Boolean(
      raw.frontendUsesPeoplePriceTiers ??
        raw.frontend_uses_people_price_tiers
    );

    return {
      id: raw.id || raw.tour_uuid || raw.tourUUID,
      title: getTitleString(raw.title || raw.name),
      slug: raw.slug,
      price: raw.price || raw.priceMin,
      duration: raw.duration, // si la necesitas
      selectedDuration: raw.duration,
      sprice,
      vehiclesSmall: raw.vehiclesSmall,
      personsInsteadTime: Boolean(
        raw.personsInsteadTime ?? raw.uses_people_instead_of_times
      ),
      durations,
      usesPeopleAndTimesPricing: usesPeopleAndTimesPricingRaw,
      uses_people_and_times_pricing: usesPeopleAndTimesPricingRaw,
      frontendUsesPeoplePriceTiers: frontendUsesPeoplePriceTiersRaw,
      frontend_uses_people_price_tiers: frontendUsesPeoplePriceTiersRaw,
      preferLowerPeoplePriceTier: Boolean(
        raw.preferLowerPeoplePriceTier ?? raw.prefer_lower_people_price_tier
      ),
      prefer_lower_people_price_tier: Boolean(
        raw.preferLowerPeoplePriceTier ?? raw.prefer_lower_people_price_tier
      ),
      // anade aqui otros campos que suelas usar
    };
  };
  // console.log("tdffghour", tour);
  const newTour = normalizeTour(tour);
  const mainContent = () => {
    return (
      <>
        {isSimple ? null : (
          <BorderBeam borderRadius="1rem" className="tour-reservation" />
        )}

        {loading ? (
          // Skeleton mientras carga la API
          <div style={{ minWidth: "200px" }}>
            <SkeletonTextLight style={{ width: "60%" }} />
            <SkeletonTextLight style={{ width: "80%" }} />
          </div>
        ) : (
          <>
            {optionalFields?.timer && (
              <>
                <CText size="large" align="center">
                  {t("head") || "Cargando"}
                </CText>

                {error ? (
                  <div className="error-message">
                    {error || "Ocurrio un error al obtener el siguiente turno."}
                  </div>
                ) : (
                  <div
                    className="countdown-timer flex flex-row justify-center"
                    style={countdownStyle}
                  >
                    {timeRemaining.days > 0 && (
                      <CText
                        size={internalCountdownStyle.size}
                        align={internalCountdownStyle.align}
                      >
                        {timeRemaining.days}d&nbsp;
                      </CText>
                    )}
                    {timeRemaining.hours > 0 && (
                      <CText
                        size={internalCountdownStyle.size}
                        align={internalCountdownStyle.align}
                      >
                        {timeRemaining.hours}h&nbsp;
                      </CText>
                    )}
                    <CText
                      size={internalCountdownStyle.size}
                      align={internalCountdownStyle.align}
                      theme={isTuk ? "light" : "dark"}
                    >
                      {timeRemaining.minutes}m {timeRemaining.seconds}s
                    </CText>
                  </div>
                )}
              </>
            )}

            <div style={{ marginTop: spacingFluidMedium }}>
              <Link
                to={`/reservation/${tour.slug}`}
                state={{
                  title: getTitleString(newTour?.title || tourTitle),
                  tour: newTour,
                  newTour,
                }}
              >
                <CButton
                  className="relative"
                  style={{
                    whiteSpace: "nowrap", // Evitar que el texto se divida en varias lineas
                  }}
                >
                  {t("content")}
                </CButton>
              </Link>
            </div>
          </>
        )}
      </>
    );
  };

  return (
    <>
      <div
        className="tour-reservation relative"
        style={{
          ...useGetTileBaseStyleDrop(),
          ...dropShadowLowStyle,
          ...tourReservationStyling,
        }}
      >
        {hasMultipleDurations ? renderDurationButtons() : mainContent()}
      </div>

      {isInfo && (
        <VitalInfoComponent
          tour={tour}
          className="vitalInfoComponentOnCallToAction"
        />
      )}
    </>
  );
};

export default CallToBook;
